import React, { useState, useEffect, useCallback } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useLocation } from 'react-router-dom';

/**
 * OnboardingTour Component
 * Provides a multi-page guided tour for first-time users using React Joyride
 * Flow: /main (Welcome + Country) → /chat (varies by mobile/desktop)
 */
const OnboardingTour = () => {
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const location = useLocation();

    // Detect mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', checkMobile);

        // Expose reset function for testing
        window.resetTour = resetOnboardingTour;

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Define steps per page
    const mainPageSteps = [
        // Step 1: Welcome (MainPage)
        {
            target: 'body',
            content: (
                <div className="text-center">
                    <div className="text-4xl mb-3">👋</div>
                    <h2 className="text-xl font-bold text-dark-green mb-2">Viza'ya Hoş Geldiniz!</h2>
                    <p className="text-gray-600 text-sm">
                        Vize başvuru sürecinizi adım adım göstereceğiz.
                    </p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
            styles: { options: { width: isMobile ? 300 : 400 } },
            disableScrolling: true,
        },
        // Step 2: Country Selection (MainPage)
        {
            target: '[data-tour="country-grid"]',
            content: (
                <div>
                    <h3 className="font-bold text-dark-green mb-2">🌍 Ülke Seçimi</h3>
                    <p className="text-gray-600 text-sm">
                        Vize başvurusu yapmak istediğiniz ülkeyi seçin.
                    </p>
                </div>
            ),
            placement: isMobile ? 'bottom' : 'top',
            disableBeacon: true,
            spotlightClicks: true,
            disableScrollParentFix: false,
            disableScrolling: false,
            styles: {
                buttonNext: { display: 'none' },
                buttonBack: { display: 'none' },
            }
        },
    ];

    // Desktop dashboard steps (with sidebar)
    const dashboardStepsDesktop = [
        {
            target: '[data-tour="sidebar-nav"]',
            content: (
                <div>
                    <h3 className="font-bold text-dark-green mb-2">📋 Menü</h3>
                    <p className="text-gray-600 text-sm">
                        Başvurularınızı, belgelerinizi ve profilinizi buradan yönetin.
                    </p>
                </div>
            ),
            placement: 'right',
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: '[data-tour="applications-link"]',
            content: (
                <div>
                    <h3 className="font-bold text-dark-green mb-2">📝 Başvurular</h3>
                    <p className="text-gray-600 text-sm">
                        Tüm vize başvurularınızı buradan takip edebilirsiniz.
                    </p>
                </div>
            ),
            placement: 'right',
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: '[data-tour="documents-link"]',
            content: (
                <div>
                    <h3 className="font-bold text-dark-green mb-2">📁 Dökümanlar</h3>
                    <p className="text-gray-600 text-sm">
                        Vize başvurunuz için gerekli belgeleri buradan yönetebilirsiniz.
                    </p>
                </div>
            ),
            placement: 'right',
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: '[data-tour="chat-link"]',
            content: (
                <div>
                    <h3 className="font-bold text-dark-green mb-2">🤖 Vize Danışmanı</h3>
                    <p className="text-gray-600 text-sm">
                        Vize AI, sorularınızı yanıtlar ve belge önerileri sunar.
                    </p>
                </div>
            ),
            placement: 'right',
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: '[data-tour="profile-link"]',
            content: (
                <div>
                    <h3 className="font-bold text-dark-green mb-2">📄 Aktif Başvurum</h3>
                    <p className="text-gray-600 text-sm">
                        Seçtiğiniz ülkeye ait aktif başvurunuzu ve sürecinizi buradan takip edin.
                    </p>
                </div>
            ),
            placement: 'right',
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: 'body',
            content: (
                <div className="text-center">
                    <div className="text-4xl mb-3">🎉</div>
                    <h2 className="text-xl font-bold text-dark-green mb-2">Hazırsınız!</h2>
                    <p className="text-gray-600 text-sm">
                        Profilinizi tamamlayarak başlayabilirsiniz.
                    </p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
            styles: { options: { width: 400 } },
            disableScrolling: true,
        },
    ];

    // Mobile dashboard steps (skip sidebar, focus on menu)
    const dashboardStepsMobile = [
        {
            target: 'body',
            content: (
                <div className="text-center">
                    <div className="text-4xl mb-3">👤</div>
                    <h2 className="text-lg font-bold text-dark-green mb-2">Profiliniz</h2>
                    <p className="text-gray-600 text-sm">
                        Buradan kişisel bilgilerinizi ve belgelerinizi yönetebilirsiniz.
                    </p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
            styles: { options: { width: 300 } }
        },
        {
            target: '[data-tour="mobile-menu-btn"]',
            content: (
                <div>
                    <h3 className="font-bold text-dark-green mb-2">📋 Menü</h3>
                    <p className="text-gray-600 text-sm">
                        Diğer sayfalara ulaşmak için menüyü kullanabilirsiniz.
                    </p>
                </div>
            ),
            placement: 'bottom',
            disableBeacon: true,
            spotlightClicks: true,
        },
        {
            target: 'body',
            content: (
                <div className="text-center">
                    <div className="text-4xl mb-3">🎉</div>
                    <h2 className="text-lg font-bold text-dark-green mb-2">Hazırsınız!</h2>
                    <p className="text-gray-600 text-sm">
                        İşlemlerinize başlayabilirsiniz!
                    </p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
            styles: { options: { width: 300 } }
        },
    ];

    // Get current page steps based on route and device
    const getCurrentSteps = useCallback(() => {
        if (location.pathname === '/main') {
            return mainPageSteps;
        } else if (location.pathname === '/profile' || location.pathname === '/chat' || location.pathname === '/applications') {
            // Run dashboard tour on any internal page if triggered
            return isMobile ? dashboardStepsMobile : dashboardStepsDesktop;
        }
        return [];
    }, [location.pathname, isMobile]);

    // Reset tour state on location change to prevent index carry-over
    useEffect(() => {
        // Only reset if moving between main and internal pages, not between internal pages
        if (location.pathname === '/main') {
            setRun(false);
            setStepIndex(0);
        }
    }, [location.pathname]);

    // Check stored tour state - with debounce to prevent flash
    useEffect(() => {
        const hasCompletedOnboarding = localStorage.getItem('viza_onboarding_completed');
        const isLoggedIn = (sessionStorage.getItem('sessionId') || localStorage.getItem('sessionId'));
        const tourPhase = localStorage.getItem('viza_onboarding_phase');

        // Don't restart tour if already running
        if (run) return;

        const mainTourSeen = sessionStorage.getItem('viza_onboarding_main_seen');

        if (isLoggedIn && !hasCompletedOnboarding && location.pathname === '/main' && !mainTourSeen) {
            const timer = setTimeout(() => {
                setStepIndex(0);
                setRun(true);
                localStorage.setItem('viza_onboarding_phase', 'main');
            }, 1000);
            return () => clearTimeout(timer);
        }

        // Only start dashboard phase if coming from main phase
        // Now supports /profile, /chat, or /applications as the entry point for phase 2
        const isInternalPage = ['/profile', '/chat', '/applications'].some(path => location.pathname.startsWith(path));

        if (isLoggedIn && !hasCompletedOnboarding && isInternalPage && tourPhase === 'main') {
            localStorage.setItem('viza_onboarding_phase', 'dashboard');
            const timer = setTimeout(() => {
                setStepIndex(0);
                setRun(true);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [location.pathname, run]);

    // Handle tour callbacks
    const handleJoyrideCallback = (data) => {
        const { action, index, status, type } = data;
        const currentSteps = getCurrentSteps();

        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            // Prevent main tour from restarting in the same session
            if (location.pathname === '/main') {
                sessionStorage.setItem('viza_onboarding_main_seen', 'true');
            }

            setRun(false);

            // Mark as completed if finishing updated dashboard tour or skipping
            const isInternalPage = ['/profile', '/chat', '/applications'].some(path => location.pathname.startsWith(path));

            if (isInternalPage || status === STATUS.SKIPPED) {
                localStorage.setItem('viza_onboarding_completed', 'true');
                localStorage.removeItem('viza_onboarding_phase');
            }
        } else if (type === EVENTS.STEP_AFTER) {
            if (action === ACTIONS.NEXT) {
                if (location.pathname === '/main' && index === currentSteps.length - 1) {
                    setRun(false);
                } else {
                    setStepIndex(index + 1);
                }
            } else if (action === ACTIONS.PREV) {
                setStepIndex(index - 1);
            }
        }
    };

    // Lock body scroll when tour is running (except for country grid step)
    useEffect(() => {
        const currentSteps = getCurrentSteps();
        const isCountryGridStep = location.pathname === '/main' &&
            stepIndex === 1 &&
            currentSteps[stepIndex]?.target === '[data-tour="country-grid"]';

        if (run && !isCountryGridStep) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [run, stepIndex, location.pathname, getCurrentSteps]);

    // Custom styles
    const joyrideStyles = {
        options: {
            primaryColor: '#C5E86C',
            textColor: '#1a4d2e',
            backgroundColor: '#ffffff',
            overlayColor: 'rgba(0, 0, 0, 0.85)', // Darker overlay
            zIndex: 10000,
        },
        buttonNext: {
            backgroundColor: '#C5E86C',
            color: '#1a4d2e',
            fontWeight: 'bold',
            borderRadius: '8px',
            padding: isMobile ? '6px 12px' : '8px 16px',
            fontSize: isMobile ? '14px' : '16px',
        },
        buttonBack: { color: '#666', marginRight: '8px' },
        buttonSkip: { color: '#999' },
        tooltip: {
            borderRadius: '12px',
            padding: isMobile ? '16px' : '20px',
        },
        spotlight: { borderRadius: '8px' },
    };

    const locale = {
        back: 'Geri',
        close: 'Kapat',
        last: location.pathname === '/main' ? 'Tamam' : 'Başla!',
        next: 'İleri',
        skip: 'Geç',
    };

    // Calculate step count display in Turkish
    const getProgressText = (current, total) => `Adım ${current}/${total}`;

    const currentSteps = getCurrentSteps();
    if (currentSteps.length === 0) return null;

    return (
        <Joyride
            callback={handleJoyrideCallback}
            continuous
            hideCloseButton
            run={run}
            scrollToFirstStep
            showSkipButton
            stepIndex={stepIndex}
            steps={currentSteps}
            styles={joyrideStyles}
            locale={locale}
            disableOverlayClose
            spotlightPadding={8}
            floaterProps={{
                disableAnimation: true,
            }}
        />
    );
};

export const resetOnboardingTour = () => {
    localStorage.removeItem('viza_onboarding_completed');
    localStorage.removeItem('viza_onboarding_phase');
    sessionStorage.removeItem('viza_onboarding_main_seen');
    window.location.reload();
};

export default OnboardingTour;
